import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { AiChatService } from '../ai-chat.service';
import { ChatMessage, ChatTab, ChatWidgetConfig } from '../ai-chat.types';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ai-chat-widget',
  templateUrl: './ai-chat-widget.component.html',
  styleUrls: ['./ai-chat-widget.component.scss']
})
export class AiChatWidgetComponent implements OnInit, OnDestroy {
  @Input() position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' = 'bottom-right';
  @Input() theme: 'light' | 'dark' = 'light';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';

  chatTabs: ChatTab[] = [];
  activeChatId: string = '';
  isMinimized: boolean = true;
  isLoading: boolean = false;
  currentMessage: string = '';
  
  private subscriptions: Subscription = new Subscription();

  constructor(private aiChatService: AiChatService) {}

  ngOnInit() {
    this.loadChatsFromStorage();
    
    if (this.chatTabs.length === 0) {
      this.createNewChat();
    } else {
      this.activeChatId = this.chatTabs[0].id;
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.saveChatsToStorage();
  }

  toggleWidget() {
    this.isMinimized = !this.isMinimized;
  }

	createNewChat() {
		const now = new Date();
		const timeString = now.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
		const newChat: ChatTab = {
			id: this.generateChatId(),
			title: `New Chat ${timeString}`,
			messages: [],
			createdAt: now,
			updatedAt: now
		};
		
		this.chatTabs.push(newChat);
		this.activeChatId = newChat.id;
		this.saveChatsToStorage();
	}  selectChat(chatId: string) {
    this.activeChatId = chatId;
  }

	closeChat(chatId: string) {
		const index = this.chatTabs.findIndex(chat => chat.id === chatId);
		if (index !== -1)
		{
			this.chatTabs.splice(index, 1);
			
			if (this.activeChatId === chatId)
			{
				if (this.chatTabs.length > 0)
				{
					this.activeChatId = this.chatTabs[0].id;
				}
				else
				{
					this.createNewChat();
				}
			}
			
			this.saveChatsToStorage();
		}
	}

	startEditingTabTitle(chatId: string) {
		const chat = this.chatTabs.find(c => c.id === chatId);
		if (chat)
		{
			chat.isEditing = true;
		}
	}

	finishEditingTabTitle(chatId: string, newTitle: string) {
		const chat = this.chatTabs.find(c => c.id === chatId);
		if (chat)
		{
			chat.isEditing = false;
			if (newTitle.trim())
			{
				chat.title = newTitle.trim();
				chat.updatedAt = new Date();
				this.saveChatsToStorage();
			}
		}
	}

	cancelEditingTabTitle(chatId: string) {
		const chat = this.chatTabs.find(c => c.id === chatId);
		if (chat)
		{
			chat.isEditing = false;
		}
	}  getActiveChat(): ChatTab | undefined {
    return this.chatTabs.find(chat => chat.id === this.activeChatId);
  }

  async sendMessage() {
    if (!this.currentMessage.trim() || this.isLoading) {
      return;
    }

    const activeChat = this.getActiveChat();
    if (!activeChat) {
      return;
    }

    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      content: this.currentMessage,
      role: 'user',
      timestamp: new Date()
    };

    activeChat.messages.push(userMessage);
    activeChat.updatedAt = new Date();
    
    const messageToSend = this.currentMessage;
    this.currentMessage = '';
    this.isLoading = true;

    try {
      const response = await this.aiChatService.sendMessage(messageToSend, activeChat.id, {
        previousMessages: activeChat.messages.slice(-10)
      }).toPromise();

      if (response.success) {
        const aiMessage: ChatMessage = {
          id: this.generateMessageId(),
          content: response.message,
          role: 'assistant',
          timestamp: new Date(),
          toolCalls: response.toolCalls
        };

        activeChat.messages.push(aiMessage);
        activeChat.updatedAt = new Date();
        
        if (activeChat.messages.length === 2) {
          activeChat.title = this.generateChatTitle(userMessage.content);
        }

        // Check if this was a tutorial request and handle it
        const isTutorialRequest = this.detectTutorialRequest(messageToSend);
        if (isTutorialRequest) {
          this.handleTutorialRequest(messageToSend, response);
        }
      } else {
        const errorMessage: ChatMessage = {
          id: this.generateMessageId(),
          content: response.message || 'I apologize, but I encountered an error. Please try again.',
          role: 'assistant',
          timestamp: new Date(),
          isError: true
        };
        activeChat.messages.push(errorMessage);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: 'I apologize, but I encountered a connection error. Please check your internet connection and try again.',
        role: 'assistant',
        timestamp: new Date(),
        isError: true
      };
      activeChat.messages.push(errorMessage);
    } finally {
      this.isLoading = false;
      this.saveChatsToStorage();
    }
  }

  clearChat() {
    const activeChat = this.getActiveChat();
    if (activeChat) {
      activeChat.messages = [];
      activeChat.updatedAt = new Date();
      this.saveChatsToStorage();
    }
  }

  exportChat() {
    const activeChat = this.getActiveChat();
    if (activeChat) {
      const chatData = {
        title: activeChat.title,
        createdAt: activeChat.createdAt,
        messages: activeChat.messages
      };
      
      const blob = new Blob([JSON.stringify(chatData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeChat.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }

  private generateChatId(): string {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private generateMessageId(): string {
    return 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

	private generateChatTitle(firstMessage: string): string {
		// Clean up the message and extract meaningful content
		const cleanMessage = firstMessage.trim().replace(/\s+/g, ' ');
		
		// If it's a question, try to extract the main topic
		if (cleanMessage.includes('?'))
		{
			const questionPart = cleanMessage.split('?')[0];
			if (questionPart.length > 5)
			{
				return questionPart.length > 35 ? questionPart.substring(0, 32) + '...' : questionPart + '?';
			}
		}
		
		// Look for common patterns and create smart titles
		const lowerMessage = cleanMessage.toLowerCase();
		
		if (lowerMessage.startsWith('help') || lowerMessage.startsWith('how'))
		{
			return cleanMessage.length > 40 ? cleanMessage.substring(0, 37) + '...' : cleanMessage;
		}
		
		if (lowerMessage.startsWith('what') || lowerMessage.startsWith('why') || lowerMessage.startsWith('when'))
		{
			return cleanMessage.length > 40 ? cleanMessage.substring(0, 37) + '...' : cleanMessage;
		}
		
		if (lowerMessage.startsWith('explain') || lowerMessage.startsWith('describe'))
		{
			return cleanMessage.length > 40 ? cleanMessage.substring(0, 37) + '...' : cleanMessage;
		}
		
		// For statements or other content, take the first meaningful part
		const words = cleanMessage.split(' ');
		if (words.length <= 6)
		{
			return cleanMessage.length > 40 ? cleanMessage.substring(0, 37) + '...' : cleanMessage;
		}
		
		// Take first 6 words for longer messages
		const shortTitle = words.slice(0, 6).join(' ');
		return shortTitle + '...';
	}

  // Handle quick tutorial request from the chat window
  handleQuickTutorialRequest(message: string) {
    this.currentMessage = message;
    this.sendMessage();
  }

  // Detect if a message is requesting tutorial help
  private detectTutorialRequest(message: string): boolean {
    const tutorialKeywords = [
      'how to', 'how do i', 'help me', 'guide me', 'show me', 'tutorial', 
      'walk me through', 'step by step', 'enroll', 'create user', 'add user',
      'create course', 'safety report', 'where is', 'find', 'locate',
      'fill out', 'form', 'button', 'click', 'submit', 'save'
    ];
    
    const lowerMessage = message.toLowerCase();
    return tutorialKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  // Handle tutorial request
  private handleTutorialRequest(userMessage: string, aiResponse: any) {
    // For Angular pages, try to use tutorial system directly
    if ((window as any).AITutorialAgent) {
      (window as any).AITutorialAgent.handleAITutorialRequest({
        userMessage: userMessage,
        intent: 'start_tutorial',
        context: {}
      });
      
      // Add a message to the chat indicating tutorial is starting
      const activeChat = this.getActiveChat();
      if (activeChat) {
        const tutorialMessage: ChatMessage = {
          id: this.generateChatId(),
          content: '🎯 Analyzing page and generating interactive tutorial... Look for highlighted elements!',
          role: 'assistant',
          timestamp: new Date(),
          isSystem: true
        };
        activeChat.messages.push(tutorialMessage);
        this.saveChatsToStorage();
      }
    } else {
      console.warn('Tutorial system not available on this page');
      
      // Add informative message
      const activeChat = this.getActiveChat();
      if (activeChat) {
        const infoMessage: ChatMessage = {
          id: this.generateChatId(),
          content: '❌ Tutorial system is not available on this page. The tutorial features work best on pages with interactive elements.',
          role: 'assistant',
          timestamp: new Date(),
          isSystem: true
        };
        activeChat.messages.push(infoMessage);
        this.saveChatsToStorage();
      }
    }
  }

  // Determine what type of tutorial the user is requesting
  private determineTutorialType(userMessage: string): string {
    const message = userMessage.toLowerCase();
    
    if (message.includes('form') || message.includes('fill out') || message.includes('enter')) {
      return 'form_guidance';
    }
    if (message.includes('button') || message.includes('click') || message.includes('where is')) {
      return 'element_finding';
    }
    if (message.includes('create') || message.includes('add') || message.includes('new')) {
      return 'creation_workflow';
    }
    if (message.includes('edit') || message.includes('update') || message.includes('modify')) {
      return 'editing_workflow';
    }
    if (message.includes('save') || message.includes('submit') || message.includes('send')) {
      return 'submission_workflow';
    }
    
    return 'general_guidance';
  }

  private loadChatsFromStorage() {
    try {
      const saved = localStorage.getItem('ai_chat_tabs');
      if (saved) {
        this.chatTabs = JSON.parse(saved).map((chat: any) => ({
          ...chat,
          createdAt: new Date(chat.createdAt),
          updatedAt: new Date(chat.updatedAt),
          messages: chat.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
      }
    } catch (error) {
      console.error('Error loading chats from storage:', error);
      this.chatTabs = [];
    }
  }

  private saveChatsToStorage() {
    try {
      localStorage.setItem('ai_chat_tabs', JSON.stringify(this.chatTabs));
    } catch (error) {
      console.error('Error saving chats to storage:', error);
    }
  }
}